// Lo que el usuario adjuntó, dibujado en SU burbuja —
// ADR-2026-09-08-agregar-productos-es-una-herramienta.
//
// El mensaje guarda un resumen sin URLs firmadas (`adjuntosDelMensaje.ts`): la imagen se
// vuelve a firmar acá por su path, el producto toma la miniatura del catálogo por su id.
// Así el chat reabierto desde el historial sigue mostrando lo que se mandó.
//
// Las que son FOTO se tocan y se abren en grande, con "Agregar al chat" como único botón
// (ADR-2026-09-14-la-foto-del-mensaje-se-vuelve-a-adjuntar). La URL que necesita ese botón
// es la MISMA que la miniatura resolvió para dibujarse, así que viaja en el propio
// `onAbrir(url)` y el visor no la vuelve a pedir: el resumen guardado no tiene URL a
// propósito, pero acá ya hay una firmada recién.
//
// El tap vive en la miniatura y no en el adjunto, y eso resuelve solo el caso feo: cuando
// la URL de un modelo venció, hoy ya cae a chip, y un chip no abre nada.

import React, { useEffect, useState } from 'react';
import {
  FileText,
  FileType2,
  Image as ImageIcon,
  Package,
  Palette,
  PersonStanding,
  Table2,
  User, Film } from 'lucide-react';
import CachedImg from '../../../components/CachedImg';
import { formatearDuracion, formatearPeso } from '../../../chat-atlas/adjuntosChatAtlas';
import {
  adjuntoEsFoto,
  adjuntoReenganchado,
  type AdjuntoDeMensaje,
} from '../adjuntosDelMensaje';
import { signReferenceImage, type ChatAttachment } from '../agentAttachments';
import { useUserProducts } from '../productsContext';
import { VisorDeAdjunto } from './VisorDeAdjunto';

/** Cómo se llama esta foto en el visor (el `alt` y el `aria-label` del diálogo). */
function nombreDeAdjunto(a: AdjuntoDeMensaje): string {
  switch (a.kind) {
    case 'image':
      return 'Imagen adjunta';
    case 'product':
    case 'model':
      return a.name;
    case 'referencia':
      return `${a.tipo === 'pose' ? 'Pose' : a.tipo === 'fondo' ? 'Fondo' : 'Moodboard'}: ${a.name}`;
    default:
      return 'Imagen';
  }
}

const MINIATURA = 'h-[72px] w-[72px] overflow-hidden rounded-xl bg-[#E9EAF0]';

/**
 * La miniatura, clickeable cuando ya tiene URL y hay adónde abrirla. Mientras la firma no
 * llegó es un `span` inerte: no hay foto que ampliar ni URL que pasarle al visor, y un
 * botón que no hace nada es peor que ninguno.
 */
const Tocable: React.FC<{
  url: string | null;
  titulo: string;
  onAbrir?: (url: string) => void;
  children: React.ReactNode;
}> = ({ url, titulo, onAbrir, children }) => {
  if (!url || !onAbrir) {
    return (
      <span className={MINIATURA} title={titulo}>
        {children}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onAbrir(url)}
      title={titulo}
      aria-label={`Ver ${titulo}`}
      className={`${MINIATURA} block transition-transform active:scale-[0.96]`}
    >
      {children}
    </button>
  );
};

const MiniaturaFirmada: React.FC<{
  storagePath: string;
  bucket?: string;
  titulo?: string;
  onAbrir?: (url: string) => void;
}> = ({ storagePath, bucket, titulo = 'Imagen adjunta', onAbrir }) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    signReferenceImage(storagePath, bucket)
      .then((u) => {
        if (vivo) setUrl(u);
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, [storagePath, bucket]);
  return (
    <Tocable url={url} titulo={titulo} onAbrir={onAbrir}>
      {url ? (
        <CachedImg src={url} alt={titulo} className="h-full w-full object-cover" />
      ) : (
        <span className="block h-full w-full animate-pulse" />
      )}
    </Tocable>
  );
};

const Chip: React.FC<{ icono: React.ReactNode; texto: string; detalle?: string }> = ({
  icono,
  texto,
  detalle,
}) => (
  <span className="inline-flex items-center gap-2 rounded-xl bg-[#E9EAF0] py-1.5 px-2.5">
    <span className="text-[#02062B]/[0.5]">{icono}</span>
    <span className="min-w-0 max-w-[160px]">
      <span className="block truncate text-[12.5px] font-medium text-brand-black">{texto}</span>
      {detalle && <span className="block text-[11px] text-[#02062B]/[0.5]">{detalle}</span>}
    </span>
  </span>
);

/** Miniatura de una URL directa (pose / fondo / modelo). Si la URL caducó —los fondos
 *  guardados vienen firmados por una hora— cae al chip con el nombre, que es lo que
 *  importa recordar. */
const MiniaturaODeChip: React.FC<{
  url: string | null;
  nombre: string;
  detalle: string;
  icono: React.ReactNode;
  onAbrir?: (url: string) => void;
}> = ({ url, nombre, detalle, icono, onAbrir }) => {
  const [rota, setRota] = useState(false);
  // Sin foto (URL vencida) queda el chip, y un chip no abre el visor: es el mismo motivo
  // por el que el tap vive en la miniatura y no en el adjunto.
  if (!url || rota) return <Chip icono={icono} texto={nombre} detalle={detalle} />;
  return (
    <Tocable url={url} titulo={`${detalle}: ${nombre}`} onAbrir={onAbrir}>
      <img
        src={url}
        alt={nombre}
        onError={() => setRota(true)}
        className="h-full w-full object-cover"
      />
    </Tocable>
  );
};

const Adjunto: React.FC<{
  a: AdjuntoDeMensaje;
  /** Sin esto las miniaturas no se tocan (lo que pasa donde nadie sabe adjuntar). */
  onAbrir?: (url: string) => void;
}> = ({ a, onAbrir }) => {
  const productos = useUserProducts();
  switch (a.kind) {
    case 'image':
      return <MiniaturaFirmada storagePath={a.storagePath} bucket={a.bucket} onAbrir={onAbrir} />;
    case 'product': {
      const src = productos.find((p) => String(p.id) === a.productId)?.imageUrl;
      return src ? (
        <Tocable url={src} titulo={a.name} onAbrir={onAbrir}>
          <CachedImg src={src} alt={a.name} className="h-full w-full object-cover" />
        </Tocable>
      ) : (
        <Chip icono={<Package className="h-4 w-4" />} texto={a.name} detalle="Producto" />
      );
    }
    case 'model':
      return (
        <MiniaturaODeChip
          url={a.imageUrl}
          nombre={a.name}
          detalle="Modelo"
          icono={<User className="h-4 w-4" />}
          onAbrir={onAbrir}
        />
      );
    case 'referencia':
      // Con `storagePath` la miniatura se vuelve a firmar, como la imagen subida: la firma
      // que traía guardada vencía en una hora y el chip quedaba roto para siempre
      // (ADR-2026-09-14-la-referencia-guardada-no-puede-ser-una-firma). Sin él —una URL
      // pública, o algo guardado antes del cambio— sigue el camino de siempre.
      if (a.storagePath) {
        return (
          <MiniaturaFirmada
            storagePath={a.storagePath}
            bucket={a.bucket}
            titulo={nombreDeAdjunto(a)}
            onAbrir={onAbrir}
          />
        );
      }
      return (
        <MiniaturaODeChip
          url={a.imageUrl ?? null}
          nombre={a.name}
          onAbrir={onAbrir}
          detalle={a.tipo === 'pose' ? 'Pose' : a.tipo === 'fondo' ? 'Fondo' : 'Moodboard'}
          icono={
            a.tipo === 'pose' ? (
              <PersonStanding className="h-4 w-4" />
            ) : a.tipo === 'moodboard' ? (
              <Palette className="h-4 w-4" />
            ) : (
              <ImageIcon className="h-4 w-4" />
            )
          }
        />
      );
    case 'moodboard':
      return (
        <Chip
          icono={<Palette className="h-4 w-4" />}
          texto={a.name}
          detalle={`Moodboard · ${a.cantidadFotos} fotos`}
        />
      );
    case 'texto':
      return (
        <Chip icono={<FileText className="h-4 w-4" />} texto={a.name} detalle={formatearPeso(a.bytes)} />
      );
    case 'pdf':
      return (
        <Chip
          icono={<FileType2 className="h-4 w-4" />}
          texto={a.name}
          detalle={`${a.paginas} pág.${a.tieneTexto ? '' : ' · escaneado'}`}
        />
      );
    case 'planilla':
      return (
        <Chip
          icono={<Table2 className="h-4 w-4" />}
          texto={a.name}
          detalle={`${a.filas} fila(s)${a.hojas > 1 ? ` · ${a.hojas} hojas` : ''}`}
        />
      );
    case 'lote':
      return (
        <Chip
          icono={<Package className="h-4 w-4" />}
          texto={`${a.cantidad} ${a.cantidad === 1 ? 'foto' : 'fotos'}`}
          detalle="Adjuntas"
        />
      );
    case 'video':
      return (
        <Chip
          icono={<Film className="h-4 w-4" />}
          texto={a.name}
          detalle={`Video${a.duracionSeg ? ` · ${formatearDuracion(a.duracionSeg)}` : ''}`}
        />
      );
  }
};

export const AdjuntosDelUsuario: React.FC<{
  adjuntos: AdjuntoDeMensaje[];
  /**
   * Volver a enganchar una foto al composer. Sin esto las miniaturas siguen siendo lo que
   * eran —dibujo— y el visor no se abre: una foto en grande con un botón que no hace nada
   * no es mejor que la miniatura (ADR-2026-09-14-la-foto-del-mensaje-se-vuelve-a-adjuntar).
   */
  onAgregarAdjunto?: (a: ChatAttachment) => void;
}> = ({ adjuntos, onAgregarAdjunto }) => {
  // La foto abierta, con la URL que su propia miniatura ya había resuelto.
  const [abierta, setAbierta] = useState<{ a: AdjuntoDeMensaje; url: string } | null>(null);
  const puedeAbrir = !!onAgregarAdjunto;
  return (
    <div className="mb-1.5 flex max-w-[80%] flex-wrap justify-end gap-1.5">
      {adjuntos.map((a, i) => (
        <Adjunto
          key={i}
          a={a}
          onAbrir={
            puedeAbrir && adjuntoEsFoto(a) ? (url) => setAbierta({ a, url }) : undefined
          }
        />
      ))}
      {abierta && (
        <VisorDeAdjunto
          url={abierta.url}
          nombre={nombreDeAdjunto(abierta.a)}
          onCerrar={() => setAbierta(null)}
          onAgregar={() => {
            const chip = adjuntoReenganchado(abierta.a, abierta.url);
            // `null` no debería salir nunca: `adjuntoEsFoto` ya dejó afuera todo lo que
            // esta función no sabe reconstruir (el modelo sin id incluido), así que la
            // miniatura ni siquiera se toca. Queda el cinturón, y cierra igual.
            if (chip) onAgregarAdjunto?.(chip);
            setAbierta(null);
          }}
        />
      )}
    </div>
  );
};

export default AdjuntosDelUsuario;
